import { ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AttachmentItem,
  isTextConversation,
  useConversation,
  useConversationMessages,
  useConversationTranscript,
} from '@/entities/conversations';
import { useTimeDisplayFormat } from '@/entities/settings';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn, formatDateTime, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { formatPhraseTimeRange } from '../../lib/formatPhraseTimeRange';

type Props = {
  /** ID диалога, чьи вложения отображаются */
  conversationId: number;
};

/**
 * Таб «Вложения» в карточке диалога.
 *
 * Отображает список вложений из ответа GET /conversation/{id} с поддержкой
 * внешних ссылок и файлов хранилища. При наличии phraseId — выводит
 * кликабельный таймстемп фразы для прокрутки транскрипта к ней, у вложения сообщения
 * текстовой коммуникации — время отправки сообщения для прокрутки переписки к нему.
 */
export const AttachmentsTab: FC<Props> = ({ conversationId }) => {
  const { t } = useTranslation('dialogs');
  const { data: conversation } = useConversation(conversationId);
  const { data: transcripts } = useConversationTranscript(conversationId);
  // Та же переписка, что в ленте: запрос общий, повторно не уходит
  const { data: messages } = useConversationMessages(conversationId, isTextConversation(conversation));
  const dialogSession = useDialogSessionOptional();
  const timeDisplayFormat = useTimeDisplayFormat();

  const attachments = conversation?.attachments ?? [];

  const phrasesById = useMemo<Map<number, { startedAt: number; finishedAt?: number | null }>>(() => {
    const map = new Map<number, { startedAt: number; finishedAt?: number | null }>();
    if (!transcripts) return map;
    for (const phrase of transcripts) {
      map.set(phrase.id, { startedAt: phrase.startedAt, finishedAt: phrase.finishedAt });
    }
    return map;
  }, [transcripts]);

  // Время отправки — тем же форматом, что над облаком сообщения в переписке
  const messageTimeLabels = useMemo(
    () => new Map((messages ?? []).map((message) => [message.id, formatDateTime(message.createdAt)])),
    [messages],
  );

  const handleScrollToPhrase = useCallback(
    (phraseId: number) => {
      dialogSession?.scrollToTranscript(String(phraseId));
    },
    [dialogSession?.scrollToTranscript],
  );

  const handleScrollToMessage = useCallback(
    (messageId: number) => {
      dialogSession?.scrollToMessage(messageId);
    },
    [dialogSession?.scrollToMessage],
  );

  if (attachments.length === 0) {
    return <div className="flex items-center justify-center p-6 text-muted-foreground">{t('attachmentsEmpty')}</div>;
  }

  return (
    <ScrollArea orientation="vertical" className={cn('flex-1 min-h-0 w-full', SCROLL_AREA_VIEWPORT_BLOCK)}>
      <div className="flex flex-col gap-3 p-4 w-full min-w-0">
        {attachments.map((attachment) => {
          const phraseInfo = attachment.phraseId != null ? phrasesById.get(attachment.phraseId) : undefined;
          const phraseTimeLabel = phraseInfo
            ? formatPhraseTimeRange(phraseInfo.startedAt, phraseInfo.finishedAt, timeDisplayFormat)
            : null;
          const messageTimeLabel =
            attachment.messageId != null ? messageTimeLabels.get(attachment.messageId) || null : null;
          return (
            <AttachmentItem
              key={attachment.id}
              attachment={attachment}
              phraseTimeLabel={phraseTimeLabel}
              onScrollToPhrase={dialogSession ? handleScrollToPhrase : undefined}
              messageTimeLabel={messageTimeLabel}
              onScrollToMessage={dialogSession ? handleScrollToMessage : undefined}
            />
          );
        })}
      </div>
    </ScrollArea>
  );
};
